/**
 * Kern, Rand, „gefällt allen ein bisschen" und das Markenversprechen, gelesen
 * aus Lauf-Detail (`readRun`) und Bericht (`report.ts`). Die Logik steht auf
 * dem Server (`supabase/functions/_shared/sim/kern.ts`); hier wird nur
 * gelesen und in Sätze gebracht.
 *
 * ALTE LÄUFE UND BERICHTE haben keines der Felder. Dann liefert jede Funktion
 * hier null, und die Seite zeigt, was sie vorher zeigte. Die Nutzlast kommt
 * aus JSON, nicht aus einem Typ: alles wird geprüft, bevor es gilt.
 */

import { locale, sprache, t } from './sprache';

export interface Gruppe {
  n: number;
  wins: { candidate: number; baseline: number };
  scores: { candidate: number | null; baseline: number | null };
  verdict: 'candidate' | 'baseline' | 'tie';
}

export interface KernRand {
  kern: Gruppe;
  rand: Gruppe;
  /**
   * Gesetzt, wenn der Text nur am Rand gewinnt. Der Wortlaut kommt vom Server
   * und ist in alten Berichten eingefroren; angezeigt wird `randWarnung`, das
   * hier ist nur der Rückfall.
   */
  warnung: string | null;
}

/** Bei Läufen ohne Vergleich: wie viele je Gruppe weitergehen würden. */
export interface KernRandWeiter {
  kern: { n: number; weiter: number };
  rand: { n: number; weiter: number };
}

export interface Muster { spanne: [number, number]; streuung: number }
export interface Bilanz { haelt: number; von: number }

const ganz = (v: unknown): v is number => typeof v === 'number' && Number.isInteger(v) && v >= 0;
const zahlOderNull = (v: unknown): number | null => (typeof v === 'number' && Number.isFinite(v) ? v : null);

const zahl = (n: number) =>
  new Intl.NumberFormat(locale(), { minimumFractionDigits: 1, maximumFractionDigits: 1 }).format(n);

function gruppe(g: any): Gruppe | null {
  if (!g || !ganz(g.n) || !ganz(g.wins?.candidate) || !ganz(g.wins?.baseline)) return null;
  const verdict = g.verdict === 'candidate' || g.verdict === 'baseline' ? g.verdict : 'tie';
  return {
    n: g.n,
    wins: { candidate: g.wins.candidate, baseline: g.wins.baseline },
    scores: { candidate: zahlOderNull(g.scores?.candidate), baseline: zahlOderNull(g.scores?.baseline) },
    verdict,
  };
}

/**
 * Gewinnt die neue Fassung in der erweiterten Zielgruppe deutlicher, als die
 * Jury bei gleicher Eingabe schwankt? Dieselbe Regel wie `randTraegt` auf dem
 * Server (`supabase/functions/_shared/sim/kern.ts`, Schwelle aus
 * `rauschen.ts`): Vorsprung über max(1, round(n / 3)). Seit 21.10.2026
 * (tappla/99takes-app#302). Gilt auch für eingefrorene Berichte: eine Warnung,
 * die nach dieser Regel im Rauschen liegt, zeigt die Seite nicht mehr.
 */
export function randTraegt(rand: Gruppe): boolean {
  return rand.wins.candidate - rand.wins.baseline > Math.max(1, Math.round(rand.n / 3));
}

/** Kern und Rand eines Vergleichs, oder null. */
export function leseKernRand(quelle: unknown): KernRand | null {
  const kr = (quelle as { kern_rand?: any } | null)?.kern_rand;
  const kern = gruppe(kr?.kern);
  const rand = gruppe(kr?.rand);
  if (!kern || !rand || !kern.n || !rand.n) return null;
  const warnung = typeof kr.warnung === 'string' && kr.warnung.trim() && randTraegt(rand) ? kr.warnung : null;
  return { kern, rand, warnung };
}

/** Kern und Rand eines Laufs ohne Vergleich, oder null. */
export function leseKernRandWeiter(quelle: unknown): KernRandWeiter | null {
  const kr = (quelle as { kern_rand?: any } | null)?.kern_rand;
  const ok = (g: any) => g && ganz(g.n) && g.n > 0 && ganz(g.weiter) && g.weiter <= g.n;
  return ok(kr?.kern) && ok(kr?.rand)
    ? { kern: { n: kr.kern.n, weiter: kr.kern.weiter }, rand: { n: kr.rand.n, weiter: kr.rand.weiter } }
    : null;
}

export function leseMuster(quelle: unknown): Muster | null {
  const m = (quelle as { muster?: any } | null)?.muster;
  const a = zahlOderNull(m?.spanne?.[0]);
  const b = zahlOderNull(m?.spanne?.[1]);
  const s = zahlOderNull(m?.streuung);
  return m?.breit_flach === true && a != null && b != null && s != null ? { spanne: [a, b], streuung: s } : null;
}

/** „hält x von n". Im Lauf-Detail unter `markenversprechen`, im Bericht je Fassung unter `versprechen`. */
export function leseBilanz(b: unknown): Bilanz | null {
  const x = b as { haelt?: unknown; von?: unknown } | null;
  return x && ganz(x.haelt) && ganz(x.von) && x.von > 0 && x.haelt <= x.von ? { haelt: x.haelt, von: x.von } : null;
}

/** Die ruhige Zeile unter dem Urteil: was der Rand gesagt hat. */
export function randZeile(kr: KernRand): string {
  const r = kr.rand;
  const stand = r.verdict === 'baseline'
    ? `${r.wins.baseline} : ${r.wins.candidate} ${t('für die Vergleichsfassung', 'for the baseline')}`
    : r.verdict === 'candidate'
      ? `${r.wins.candidate} : ${r.wins.baseline} ${t('für den Text', 'for the text')}`
      : `${r.wins.candidate} : ${r.wins.baseline}, ${t('unentschieden', 'tie')}`;
  const punkte = r.scores.candidate != null && r.scores.baseline != null
    ? t(`, ${zahl(r.scores.candidate)} zu ${zahl(r.scores.baseline)} Punkte`, `, ${zahl(r.scores.candidate)} to ${zahl(r.scores.baseline)} points`)
    : '';
  return t(`Erweiterte Zielgruppe: ${stand}${punkte}. Zählt nicht ins Urteil.`, `Extended audience: ${stand}${punkte}. Does not count toward the verdict.`);
}

export function randZeileWeiter(kr: KernRandWeiter): string {
  return t(
    `Kernzielgruppe: ${kr.kern.weiter} von ${kr.kern.n} gehen weiter. erweiterte Zielgruppe: ${kr.rand.weiter} von ${kr.rand.n}, zählt nicht ins Urteil.`,
    `Core audience: ${kr.kern.weiter} of ${kr.kern.n} would continue. Extended audience: ${kr.rand.weiter} of ${kr.rand.n}, does not count toward the verdict.`,
  );
}

/**
 * Die Warnung „gewinnt nur am Rand“ als konkreter Satz aus den Zahlen (seit
 * 12.10.2026). ANLASS: ein Leser des Magnolia-Berichts verstand den
 * Server-Satz „Der Text gewinnt nur am Rand … Das Urteil folgt dem Kern.“
 * nicht. Der Wortlaut des Servers steckt in jedem eingefrorenen Bericht; die
 * Seite baut den Satz deshalb selbst, mit Zahlen und dem Namen der
 * Vergleichsfassung. Null, wenn die Zahlen den Fall nicht hergeben; dann zeigt
 * die Seite `kr.warnung`, wenn es eine gibt.
 *
 * `vergleich` steht im Akkusativ („die heutige Seite“). `urteile`: in der
 * Matrix zählen die Gruppen Urteile, nicht Personas (eine Persona urteilt
 * mehrmals).
 */
export function randWarnung(kr: KernRand, opt: { vergleich?: string; urteile?: boolean } = {}): string | null {
  const { kern, rand } = kr;
  if (rand.verdict !== 'candidate' || kern.verdict === 'candidate' || !randTraegt(rand)) return null;
  if (sprache() === 'en') return randWarnungEn(kr, opt);
  const vergleich = opt.vergleich?.trim() || 'die Vergleichsfassung';
  const rc = rand.wins.candidate;
  const kc = kern.wins.candidate;
  const kb = kern.wins.baseline;
  const satz1 = opt.urteile
    ? `Die neue Fassung überzeugt vor allem die erweiterte Zielgruppe: ${rc} von ${rand.n} ihrer Urteile ${rc === 1 ? 'spricht' : 'sprechen'} für sie.`
    : `Die neue Fassung überzeugt vor allem die erweiterte Zielgruppe: ${rc} von ${rand.n} ${rc === 1 ? 'zieht' : 'ziehen'} sie vor.`;
  let satz2: string;
  if (opt.urteile) {
    satz2 = kc === 0
      ? `Alle ${kern.n} Urteile der Kernzielgruppe sprechen für ${vergleich}.`
      : `Von den ${kern.n} Urteilen der Kernzielgruppe ${kc === 1 ? 'spricht' : 'sprechen'} ${kc} für sie und ${kb} für ${vergleich}.`;
  } else if (kern.n === 1) {
    satz2 = `Die eine Persona aus der Kernzielgruppe zieht ${vergleich} vor.`;
  } else {
    satz2 = kc === 0
      ? `Alle ${kern.n} Personas aus der Kernzielgruppe ziehen ${vergleich} vor.`
      : `Von den ${kern.n} Personas aus der Kernzielgruppe ${kc === 1 ? 'zieht' : 'ziehen'} ${kc} sie vor und ${kb} ${vergleich}.`;
  }
  const patt = kern.verdict === 'tie' ? ' Das ist unentschieden.' : '';
  return `${satz1} ${satz2}${patt} Gezählt wird die Kernzielgruppe.`;
}

/** Dieselbe Warnung auf Englisch, gleiche Fälle. */
function randWarnungEn(kr: KernRand, opt: { vergleich?: string; urteile?: boolean }): string {
  const { kern, rand } = kr;
  const vergleich = opt.vergleich?.trim() || 'the baseline';
  const rc = rand.wins.candidate;
  const kc = kern.wins.candidate;
  const kb = kern.wins.baseline;
  const favor = (n: number) => (n === 1 ? 'favors' : 'favor');
  const prefer = (n: number) => (n === 1 ? 'prefers' : 'prefer');
  const satz1 = opt.urteile
    ? `The new version mainly convinces the extended audience: ${rc} of its ${rand.n} ${rand.n === 1 ? 'verdict' : 'verdicts'} ${favor(rc)} it.`
    : `The new version mainly convinces the extended audience: ${rc} of ${rand.n} ${prefer(rc)} it.`;
  let satz2: string;
  if (opt.urteile) {
    satz2 = kc === 0
      ? `All ${kern.n} verdicts from the core audience favor ${vergleich}.`
      : `Of the ${kern.n} verdicts from the core audience, ${kc} ${favor(kc)} it and ${kb} ${favor(kb)} ${vergleich}.`;
  } else if (kern.n === 1) {
    satz2 = `The one persona from the core audience prefers ${vergleich}.`;
  } else {
    satz2 = kc === 0
      ? `All ${kern.n} personas from the core audience prefer ${vergleich}.`
      : `Of the ${kern.n} personas from the core audience, ${kc} ${prefer(kc)} it and ${kb} ${prefer(kb)} ${vergleich}.`;
  }
  const patt = kern.verdict === 'tie' ? ' That is a tie.' : '';
  return `${satz1} ${satz2}${patt} The core audience is what counts.`;
}

/** Hinweiszeile zum Muster. Die Grenzen stehen in kern.ts, kalibriert an 114 Läufen. */
export function musterSatz(m: Muster): string {
  const [a, b] = m.spanne;
  if (sprache() === 'en') {
    const lage = a === b ? `all scores at ${zahl(a)}` : `all scores between ${zahl(a)} and ${zahl(b)}`;
    return `Everyone likes it a little: ${lage}, nobody gives 7 or more, and no persona picks the text with conviction.`;
  }
  const lage = a === b ? `alle Noten bei ${zahl(a)}` : `alle Noten zwischen ${zahl(a)} und ${zahl(b)}`;
  return `Gefällt allen ein bisschen: ${lage}, niemand vergibt 7 oder mehr, und keine Persona wählt den Text mit Überzeugung.`;
}

export function bilanzSatz(b: Bilanz): string {
  if (sprache() === 'en') {
    return b.von === 1
      ? `${b.haelt} of 1 persona says the text keeps the promise.`
      : `${b.haelt} of ${b.von} personas say the text keeps the promise.`;
  }
  return b.von === 1
    ? `${b.haelt} von 1 Persona sagt, der Text hält es.`
    : `${b.haelt} von ${b.von} Personas sagen, der Text hält es.`;
}

/** Kern zuerst, Rand danach; innerhalb jeder Gruppe bleibt die Reihenfolge. */
export function kernZuerst<T extends { rolle?: string }>(liste: T[]): T[] {
  return [...liste.filter(x => x.rolle !== 'rand'), ...liste.filter(x => x.rolle === 'rand')];
}
